import React, { useCallback, useEffect, useState } from "react";

import { IconButton, Drawer } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";

import { useNotify, Button } from "react-admin";
import { API, graphqlOperation } from "aws-amplify";
import { helpPageByUrl } from "../../graphql/queries";
import { HelpViewer } from "./HelpViewer/HelpViewer";
import { useHistory, useLocation } from "react-router-dom/cjs/react-router-dom";
import { cleanUrlLink, isChildNull } from "../../utils/utils";
import { getEnv, logError } from "../../utils/appUtils";
import { Typography } from "@material-ui/core";
import { CloseIcon, HelpIcon } from "./iconography";

const ComingSoon = ({ url }) => {
  useEffect(() => {
    logError(
      "HelpPage:EmptyHelpPage",
      new Error(`Coming soon content detected at ${url}`)
    );
  }, [url]);

  return <Typography>Coming Soon</Typography>;
};

const useStyles = makeStyles((theme) => ({
  help: {
    margin: theme.spacing(2),
  },
  helpHeader: {
    marginLeft: theme.spacing(2),
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  helpHeaderMobile: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    margin: theme.spacing(2),
    width: "100%",
  },
  helpMobile: {
    margin: theme.spacing(2),
    width: "100%",
  },
  container: {
    marginRight: theme.spacing(2),
  },
  drawerPaper: {
    top: "48px",
    height: "calc(100% - 58px)",
    width: "30%",
    borderRadius: "5px",
    margin: "5px",
    display: "flex",
    flexDirection: "column",
  },
  drawerMobile: {
    top: "55px",
    height: "calc(100% - 58px)",
    width: "98%",
    borderRadius: "5px",
    margin: "5px",
    display: "flex",
    flexDirection: "column",
  },
  line: {
    marginTop: "0px",
    width: "94%",
    border: "none",
    borderTop: "1px solid #ccc",
    marginLeft: "3%",
  },
  title: {
    marginBottom: "0px",
    marginTop: "0px",
  },
}));

const getHelpPageByUrl = async (url, notify) => {
  try {
    const input = {
      active: "true",
      url: {
        eq: url,
      },
    };
    // `isLive` gates release visibility in prod-like envs (prod + hotfix/staging).
    // Only dev is allowed to show draft docs (active but not live).
    const env = getEnv();
    if (env !== "dev") input.filter = { isLive: { eq: true } };
    const response = await API.graphql(graphqlOperation(helpPageByUrl, input));
    const helpPageData = response.data?.helpPageByUrl?.items?.[0];
    return helpPageData;
  } catch (error) {
    logError("HelpButton:getHelpPageByUrl", error, notify);
  }
};

export const HelpButton = ({
  helpClasses,
  children,
  dynamicContentComponent,
}) => {
  const notify = useNotify();
  const history = useHistory();
  const location = useLocation();
  const [helpPage, setHelpPage] = useState(null);

  const pathname = history.location.pathname;
  const searchParams = new URLSearchParams(location.search);
  const tabNumber = searchParams.get("t");

  const memoizeGetHelpPage = useCallback(
    async (url) => {
      setHelpPage(null);
      const fetchedHelpPage = await getHelpPageByUrl(url, notify);
      setHelpPage(fetchedHelpPage);
    },
    [notify]
  );

  useEffect(() => {
    const url = `${pathname}${tabNumber ? `/${tabNumber}` : ""}`;
    const cleanedUrl = cleanUrlLink(url);
    memoizeGetHelpPage(cleanedUrl);
  }, [memoizeGetHelpPage, tabNumber, pathname]);

  const classes = useStyles();
  helpClasses = helpClasses ? helpClasses : classes;
  const isSmall = useMediaQuery((theme) => theme.breakpoints.down("xs"));
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? "popover-help" : undefined;

  const isEmptyChildren = isChildNull(children);

  return (
    <div className={classes.container}>
      <Button label="Help" onClick={handleClick}>
        <HelpIcon fontSize="small" />
      </Button>
      <Drawer
        id={id}
        open={open}
        anchorEl={anchorEl}
        anchor="right"
        onClose={handleClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        PaperProps={{
          sx: { width: "90%" },
        }}
        classes={{
          paper: isSmall ? classes.drawerMobile : classes.drawerPaper,
        }}
        ModalProps={{
          BackdropProps: {
            invisible: true,
          },
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <div
          className={isSmall ? classes.helpHeaderMobile : classes.helpHeader}
        >
          <h2 className={classes.title}>Help center</h2>
          <IconButton aria-label="close" onClick={handleClose}>
            <CloseIcon fontSize="large" />
          </IconButton>
        </div>
        <hr className={classes.line} />
        <div className={isSmall ? classes.helpMobile : classes.help}>
          {!helpPage &&
            (isEmptyChildren ? (
              <ComingSoon
                url={`${pathname}${tabNumber ? `/${tabNumber}` : ""}`}
              />
            ) : (
              children
            ))}
          {helpPage && (
            <HelpViewer handleClose={handleClose} helpPage={helpPage}>
              {dynamicContentComponent}
            </HelpViewer>
          )}
        </div>
      </Drawer>
    </div>
  );
};

export const HelpIconButton = ({ helpClasses, children }) => {
  const classes = useStyles();
  helpClasses = helpClasses ? helpClasses : classes;
  const isSmall = useMediaQuery((theme) => theme.breakpoints.down("xs"));
  const [anchorEl, setAnchorEl] = React.useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? "audit-help" : undefined;

  return (
    <div className={classes.container}>
      <IconButton edge="start" onClick={handleClick}>
        <HelpIcon variant="outlined" />
      </IconButton>
      <Drawer
        id={id}
        open={open}
        anchorEl={anchorEl}
        anchor="right"
        classes={{ paper: classes.drawerMobile }}
        ModalProps={{
          BackdropProps: {
            invisible: true,
          },
        }}
        PaperProps={{
          sx: { width: "90%" },
        }}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <div className={isSmall ? classes.helpMobile : classes.help}>
          {children}
        </div>
      </Drawer>
    </div>
  );
};
